import { daysSince } from '../core/format/dates'
import type { AccountOrder } from '../core/types/account'

/**
 * Bestellingen in Mijn omgeving: welke "volgt" ze nog (onderweg, of net bezorgd), en hoe je
 * er rechtstreeks naartoe linkt. Pure functies, zonder React.
 *
 * Twee soorten vallen buiten "wacht op een pakket":
 * - geannuleerd: er komt niets meer;
 * - alleen cadeaubonnen: er gaat niets op de post (de bon komt per mail), dus de status blijft
 *   "In behandeling" zonder dat er nog iets gebeurt.
 */

/** Zo lang na de besteldatum blijft een afgeronde bestelling nog op het overzicht staan. */
export const RECENT_DAYS = 7

type IsGiftCard = (productId: string) => boolean
const noGiftCards: IsGiftCard = () => false

/** Uit de catalogus: gaat dit product niet op de post (cadeaubon of vooruitbetaalde kuur)? */
export const giftCardCheck =
  (productById: (id: string) => { kind?: string } | undefined): IsGiftCard =>
  (id) => {
    const kind = productById(id)?.kind
    return kind === 'giftcard' || kind === 'kuur'
  }

/** Alleen cadeaubonnen of kuren: er gaat niets op de post. */
export const isGiftCardOnly = (o: AccountOrder, isGiftCard: IsGiftCard) => o.lines.length > 0 && o.lines.every((l) => isGiftCard(l.productId))

/** Er komt nog een pakket: niet bezorgd, niet geannuleerd, en er zit iets in om te versturen. */
export const isAwaitingDelivery = (o: AccountOrder, isGiftCard: IsGiftCard = noGiftCards) =>
  o.status !== 'Bezorgd' && !o.cancelledAt && !isGiftCardOnly(o, isGiftCard)

/**
 * De bestellingen die op het overzicht horen, nieuwste eerst: elk pakket dat nog komt, plus wat
 * kort geleden besteld is (net bezorgd, een cadeaubon). Geannuleerd: nooit. Er is (nog) geen
 * bezorgdatum, dus "kort geleden" telt vanaf de besteldatum.
 */
export function ordersToFollow(orders: AccountOrder[], isGiftCard: IsGiftCard = noGiftCards): AccountOrder[] {
  return orders
    .filter((o) => !o.cancelledAt && (isAwaitingDelivery(o, isGiftCard) || daysSince(o.date) <= RECENT_DAYS))
    .sort((a, b) => b.date.localeCompare(a.date) || b.id.localeCompare(a.id))
}

/**
 * Hoe een bestelling heet voor de klant en voor Sarah: "#1042". Nooit het technische id; zonder
 * nummer (nog niet betaald, of een server zonder dit veld) "Nummer volgt in de mail".
 */
export const orderLabel = (o: Pick<AccountOrder, 'number'>) => (o.number?.trim() ? `#${o.number.trim()}` : 'Nummer volgt in de mail')

/** Betaald = het factuurnummer is toegekend (dat gebeurt bij betalen). */
export const isPaidOrder = (o: Pick<AccountOrder, 'number'>) => Boolean(o.number?.trim())

/** Een factuur alleen bij een betaalde, niet geannuleerde bestelling. */
export const hasInvoice = (o: Pick<AccountOrder, 'number' | 'cancelledAt'>) => isPaidOrder(o) && !o.cancelledAt

/** Het anker van één bestelling op de pagina Bestellingen. */
export const orderAnchor = (id: string) => `bestelling-${id}`

/** Rechtstreeks naar één bestelling. */
export const orderHref = (id: string) => `/account/bestellingen#${orderAnchor(id)}`
